import { useState, useEffect, useCallback } from 'react';

interface IBudgetPercentageInputProps {
  value: number | string;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  isRemoved?: boolean;
  max?: number;
}

const formatPercentageDisplay = (value: number): string => {
  if (value === 0) {
    return '';
  }

  if (value % 1 === 0) {
    return `${value}%`;
  }

  const formatted = value.toFixed(2).replace('.', ',');
  return `${formatted}%`;
};

const parseFromNumericValue = (value: string | number): number => {
  if (typeof value === 'number') {
    return value;
  }
  
  if (!value) {
    return 0;
  }

  let cleanValue = value.toString().replace(/%/g, '').trim();
  cleanValue = cleanValue.replace(/[^\d.,]/g, '');
  cleanValue = cleanValue.replace(',', '.');
  
  if (!cleanValue) {
    return 0;
  }

  const parsed = Number.parseFloat(cleanValue);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const BudgetPercentageInput = (props: IBudgetPercentageInputProps) => {
  const [displayValue, setDisplayValue] = useState<string>('');
  const [rawValue, setRawValue] = useState<number>(0);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  useEffect(() => {
    if (!isFocused) {
      if (props.value !== undefined && props.value !== null && props.value !== '' && props.value !== 0) {
        const numericValue = typeof props.value === 'number' 
          ? props.value 
          : parseFromNumericValue(props.value.toString());
        
        if (numericValue > 0) {
          setRawValue(numericValue);
          setDisplayValue(formatPercentageDisplay(numericValue));
        } else {
          setRawValue(0);
          setDisplayValue('');
        }
      } else {
        setRawValue(0);
        setDisplayValue('');
      }
    }
  }, [props.value, isFocused]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      
      if (!inputValue) {
        setRawValue(0);
        setDisplayValue('');
        props.onChange(0);
        return;
      }

      let cleanValue = inputValue.replace(/%/g, '').trim();
      cleanValue = cleanValue.replace(/[^\d.,]/g, '');
      
      if (!cleanValue) {
        setRawValue(0);
        setDisplayValue('');
        props.onChange(0);
        return;
      }

      let normalizedValue = cleanValue.replace(',', '.');
      const parts = normalizedValue.split('.');
      
      if (parts.length > 2) {
        return;
      }

      if (parts[1] && parts[1].length > 2) {
        parts[1] = parts[1].substring(0, 2);
      }

      normalizedValue = parts.join('.');
      const percentage = Number.parseFloat(normalizedValue);
      
      if (Number.isNaN(percentage)) {
        return;
      }

      const limitedPercentage = props.max !== undefined 
        ? Math.min(percentage, props.max) 
        : percentage;

      setRawValue(limitedPercentage);
      
      const displayText = limitedPercentage !== percentage && props.max !== undefined
        ? (limitedPercentage % 1 === 0 
            ? String(limitedPercentage)
            : limitedPercentage.toFixed(2).replace('.', ','))
        : (cleanValue.includes(',') 
            ? cleanValue 
            : parts.length > 1 && parts[1]
              ? `${parts[0]},${parts[1]}`
              : parts[0]);
      
      setDisplayValue(displayText);
      props.onChange(limitedPercentage);
    },
    [props]
  );

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (rawValue === 0) {
      setDisplayValue('');
      props.onChange(0);
    } else {
      setDisplayValue(formatPercentageDisplay(rawValue));
      props.onChange(rawValue);
    }
  }, [rawValue, props]);

  const handleFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    const currentValue = parseFromNumericValue(props.value);
    if (currentValue > 0) {
      const valueStr = currentValue % 1 === 0 
        ? String(currentValue)
        : currentValue.toFixed(2).replace('.', ',');
      setDisplayValue(valueStr);
    }
    e.target.select();
  }, [props.value]);

  return (
    <input
      type="text"
      className={`form-control text-right ${props.className || ''} ${props.isRemoved ? '!bg-red-50' : ''}`}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      placeholder={props.placeholder || '00%'}
      disabled={props.disabled}
      style={props.isRemoved ? { backgroundColor: '#fef2f2' } : undefined}
    />
  );
};

export default BudgetPercentageInput;
